import {Button} from '@wandb/components';
import React, {FC, memo, useContext} from 'react';
import {useParams} from 'react-router-dom';

import CreateProjectDrawer from '../pages/HomePage/HomePageSidebar/CreateProjectDrawer';
import {useViewer} from '../state/viewer/hooks';
import {useEntitySettings} from '../util/entitySettings';
import {useRampFlagAccountSelector} from '../util/rampFeatureFlags';
import ImageIconHeader from './ImageIconHeader';
import {useNavContext, useNavUpdaterContext} from './NavContextProvider';
import {
  AccountProjectPaginatedList,
  TeamProjectPaginatedList,
  UserProjectPaginatedList,
} from './PaginatedList/PaginatedProjectLists';
import {AccountSelectorContext} from './Search/SearchNav/AccountSelectorContextProvider';
import {Account, AccountType} from './Search/SearchNav/types';
import {SectionTopHeader} from './Section/SectionNew';

export type ProjectsTableHeaderProps = {
  entityName: string;
  readOnly?: boolean;
  isCurrentViewerProfilePage?: boolean;
  enableAccountSelector?: boolean;
  selectedAccount?: Account | null;
};
export const ProjectsTableHeader = ({
  entityName,
  readOnly,
}: ProjectsTableHeaderProps) => {
  const {isCreatingProject} = useNavContext();
  const {toggleCreateProjectDrawer} = useNavUpdaterContext();
  return (
    <>
      <ImageIconHeader icon="project" text="Projects" />
      {!readOnly && (
        <>
          <Button
            variant="ghost"
            startIcon="add-new"
            onPress={toggleCreateProjectDrawer}>
            New project
          </Button>
          <CreateProjectDrawer
            open={isCreatingProject}
            entityName={entityName}
            toggleProjectDrawer={toggleCreateProjectDrawer}
          />
        </>
      )}
    </>
  );
};

const ProjectsTableHeaderNewComp: FC<ProjectsTableHeaderProps> = ({
  entityName,
  readOnly,
  isCurrentViewerProfilePage,
  enableAccountSelector,
  selectedAccount,
}) => {
  const {isCreatingProject} = useNavContext();
  const {toggleCreateProjectDrawer} = useNavUpdaterContext();
  return (
    <SectionTopHeader>
      {isCurrentViewerProfilePage && enableAccountSelector
        ? `Your ${
            selectedAccount?.accountType === AccountType.Personal
              ? 'personal'
              : selectedAccount?.name
          } projects`
        : 'Projects'}
      {!readOnly && (
        <>
          <Button startIcon="add-new" onPress={toggleCreateProjectDrawer}>
            New project
          </Button>
          <CreateProjectDrawer
            open={isCreatingProject}
            entityName={entityName}
            toggleProjectDrawer={toggleCreateProjectDrawer}
          />
        </>
      )}
    </SectionTopHeader>
  );
};

export const ProjectsTableHeaderNew = memo(ProjectsTableHeaderNewComp);

export type ProjectsTableProps = {
  className?: string;
  entityName: string;
  readOnly: boolean;
  suppressHeader?: boolean;
  pageSize?: number | null;
  onChangePageSize?: (pageSize: number) => void;
};

const ProjectsTable: React.FC<ProjectsTableProps> = props => {
  const enableAccountSelector = useRampFlagAccountSelector();
  const {selectedAccount} = useContext(AccountSelectorContext);
  const viewer = useViewer();
  const {entityName} = useParams<{entityName: string}>();
  const {entitySettings} = useEntitySettings(entityName);
  const isCurrentViewerProfilePage =
    viewer?.username && entityName === viewer.username;

  if (
    enableAccountSelector &&
    isCurrentViewerProfilePage &&
    selectedAccount?.accountType !== AccountType.Personal &&
    entitySettings?.entityProfileEnabled
  ) {
    return (
      <AccountProjectPaginatedList
        orgID={selectedAccount?.id ?? ''}
        userID={viewer?.id ?? ''}
        isReadOnly={props.readOnly}
      />
    );
  }
  if (
    enableAccountSelector &&
    isCurrentViewerProfilePage &&
    selectedAccount?.accountType === AccountType.Personal &&
    entitySettings?.entityProfileEnabled
  ) {
    return (
      <TeamProjectPaginatedList
        entityName={props.entityName}
        isReadOnly={props.readOnly}
      />
    );
  }

  return (
    <UserProjectPaginatedList
      entityName={props.entityName}
      isReadOnly={props.readOnly}
    />
  );
};

export default ProjectsTable;
